import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Archive as ArchiveIcon,
  Backup as BackupIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { useSettings } from '../../contexts/SettingsContext';
import { usePermission } from '../../hooks/usePermission';

const DEFAULT_DATA_MANAGEMENT = {
  retention: { detections: 365, cases: 1095, auditLogs: 2555, enforce: false },
  archival: { enabled: true }
};

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const toInt = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
};

const formatNumber = (n) => (typeof n === 'number' ? n.toLocaleString() : '0');

/** Progress bar for one usage metric; a limit of -1 means unlimited. */
const UsageBar = ({ label, used = 0, limit, unit = '' }) => {
  const unlimited = limit === -1 || limit === undefined || limit === null;
  const percent = unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const color = percent >= 90 ? 'error' : percent >= 75 ? 'warning' : 'primary';
  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {formatNumber(used)}{unit} of {unlimited ? 'unlimited' : `${formatNumber(limit)}${unit}`}
          {!unlimited && ` (${percent}%)`}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={unlimited ? 0 : percent}
        color={color}
        sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
      />
    </Box>
  );
};

const DataManagement = () => {
  const { systemSettings, companyConfig, updateCompanyConfiguration, loading } = useSettings();
  const { canManageCompany } = usePermission();
  const navigate = useNavigate();

  const [dataManagement, setDataManagement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);

  useEffect(() => {
    if (systemSettings || companyConfig) {
      // Platform defaults (system settings) overridden by this organisation's own policy (company configuration)
      const platform = (systemSettings && systemSettings.dataManagement) || {};
      const own = (companyConfig && companyConfig.dataManagement) || {};
      setDataManagement({
        retention: { ...DEFAULT_DATA_MANAGEMENT.retention, ...(platform.retention || {}), ...(own.retention || {}) },
        archival: { ...DEFAULT_DATA_MANAGEMENT.archival, ...(platform.archival || {}), ...(own.archival || {}) }
      });
    }
  }, [systemSettings, companyConfig]);

  const loadUsage = useCallback(async () => {
    try {
      setUsageLoading(true);
      setUsageError(null);
      const response = await apiService.billing.getUsage();
      setUsage(response.data.usage);
    } catch (err) {
      if (err?.response?.status === 403) {
        setUsageError('Usage statistics are only available to company administrators.');
      } else {
        setUsageError(serverMessage(err, 'Failed to load usage statistics'));
      }
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  const setField = (section, field, value) => {
    setDataManagement(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await updateCompanyConfiguration({ dataManagement });
      setSuccess('Data management policy saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(serverMessage(err, 'Failed to save data management settings. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!dataManagement) {
    return (
      <Alert severity="warning">
        Data management settings are unavailable. The system settings could not be loaded; this usually means
        your role does not include company settings access, or the settings service is unreachable.
      </Alert>
    );
  }

  const readOnly = !canManageCompany;
  const current = usage?.current || {};
  const limits = usage?.limits || {};

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        A retention job runs <strong>daily at 03:17 UTC</strong>. Audit logs, enrichment logs, read notifications and
        caches older than their retention are always pruned. Closed detections and cases are only deleted when
        <strong> Enforce retention</strong> is switched on below; with archival enabled they are written to Excubia's
        archive storage first so support can restore them. Backups are managed in the <strong>Backups</strong> tab.
      </Alert>

      <Grid container spacing={3}>
        {/* Real usage from the billing service */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<StorageIcon />}
              title="Usage"
              subheader={usage?.subscription?.plan ? `Current plan: ${usage.subscription.plan}` : 'Current usage against plan limits'}
              action={
                <Button startIcon={<RefreshIcon />} size="small" onClick={loadUsage} disabled={usageLoading}>
                  Refresh
                </Button>
              }
            />
            <CardContent>
              {usageLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
              ) : usageError ? (
                <Alert severity="info">{usageError}</Alert>
              ) : (
                <>
                  <UsageBar label="Users" used={current.currentUsers} limit={limits.maxUsers} />
                  <UsageBar label="Detections" used={current.currentDetections} limit={limits.maxDetections} />
                  <UsageBar label="Cases" used={current.currentCases} limit={limits.maxCases} />
                  <UsageBar
                    label="Storage"
                    used={current.storageUsedMB}
                    limit={limits.storageGB === -1 ? -1 : (limits.storageGB || 0) * 1024}
                    unit=" MB"
                  />
                  {usage?.subscription?.status && (
                    <Chip size="small" label={`Subscription: ${usage.subscription.status}`} variant="outlined" />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Retention policy */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<ScheduleIcon />} title="Data Retention" subheader="How long closed records and logs are kept" />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(dataManagement.retention.enforce)}
                    onChange={(e) => setField('retention', 'enforce', e.target.checked)}
                    disabled={readOnly}
                  />
                }
                label="Enforce retention (delete resolved / closed detections and cases past their period)"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Open records are never deleted. Audit and enrichment logs are pruned by the audit-log period regardless of this switch.
              </Typography>
              <Grid container spacing={2}>
                {[
                  ['detections', 'Detections (days after closure)', 30, 3650],
                  ['cases', 'Cases (days after closure)', 90, 3650],
                  ['auditLogs', 'Audit & enrichment logs (days)', 365, 3650]
                ].map(([field, label, min, max]) => (
                  <Grid item xs={12} sm={6} key={field}>
                    <TextField
                      fullWidth
                      label={label}
                      type="number"
                      value={dataManagement.retention[field]}
                      onChange={(e) => setField('retention', field, toInt(e.target.value, min))}
                      inputProps={{ min, max }}
                      disabled={readOnly}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Archival before deletion */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<ArchiveIcon />} title="Archive before deletion" subheader="Keep a copy of anything retention removes" />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={dataManagement.archival.enabled !== false}
                    onChange={(e) => setField('archival', 'enabled', e.target.checked)}
                    disabled={readOnly}
                  />
                }
                label="Write closed records to archive storage before retention deletes them"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Archived records are stored in Excubia's archive bucket (tiered to cold storage after 30 and 120 days,
                kept for two years) and can be restored by Excubia support on request. They are not counted against your plan.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Backups live in their own tab */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader avatar={<BackupIcon />} title="Backups" subheader="Exports, weekly automatic backups and restore" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Download or store a full export of your organisation's data, schedule weekly automatic exports and
                restore from any export, all from the Backups tab.
              </Typography>
              <Button variant="outlined" startIcon={<BackupIcon />} onClick={() => navigate('/dashboard/settings?tab=backups')}>
                Open Backups
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={readOnly ? 'Requires company management permission' : ''}>
          <span>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || readOnly}
              size="large"
            >
              {saving ? 'Saving...' : 'Save Data Management Policy'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DataManagement;
